import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';

// The timeline logger's focus cursor: which exercise is active, which set is
// selected, and whether the roster is expanded. Held HERE — called from the
// LoggerScreen body, ABOVE the <Modal> — rather than inside useTimelineLogger,
// which runs inside the Modal. Minimize toggles the Modal's visible=false and
// RN unmounts its whole subtree, which used to destroy the cursor useState so
// resume reopened without your place (LG-13). The draft already lives above the
// Modal (useLoggerScreen) for the same reason (LG-10c); the cursor now shares
// that durable home and survives minimize -> resume.
//
// LoggerScreen is a persistent singleton (the host never remounts, only the
// Modal's visibility toggles), so unlike the old in-Modal useState this state
// no longer gets a free reset between workouts. We reset it explicitly when a
// new session starts, keyed on the session key (sessionKeyOf, #418; a start-time correction
// does not reset it), the same signal the rest timer
// (useLoggerScreen) uses. A new session COLLAPSES the
// roster (PE-31 — it used to re-expand it) and drops any stale set selection;
// activeId is already guarded by resolvedActiveId but is reset here for
// cleanliness. Minimize -> resume keeps the SAME key, so it does NOT
// reset — that is exactly the survival LG-13 needs. (Cold boot resets it too,
// which is correct: Option B, no auto-focus on rehydrate, you re-pick.)
export type LoggerCursor = {
  activeId: string | null;
  setActiveId: Dispatch<SetStateAction<string | null>>;
  selectedSetIdx: number | null;
  setSelectedSetIdx: Dispatch<SetStateAction<number | null>>;
  rosterExpanded: boolean;
  setRosterExpanded: Dispatch<SetStateAction<boolean>>;
};

export function useLoggerCursor(sessionKey: string): LoggerCursor {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSetIdx, setSelectedSetIdx] = useState<number | null>(null);
  // ⭐ PE-31 (Antenore, 29/08) — COLLAPSED. It used to open expanded, on the
  // theory that a wrapped grid of every exercise is a self-teaching first
  // screen; his answer after living with it is «credo sia meglio … collassato
  // per default», and he asked for it in the logger as well as in the editor.
  // ⛔ Collapsed is not hidden: the strip is still there, as one horizontal
  // scroller, and the ＋ keeps its fixed slot. What it stops doing is spending
  // three or four lines of the first screen before anything has been chosen.
  // ⇒ From here on the chevron is the ONLY thing that expands the roster.
  // Nothing in the app opens it for you, which is why the three RESET paths
  // below and in useTimelineLogger (new session · card closed · exercise
  // removed) all land collapsed too: "nothing selected" has to look the same
  // however you got there.
  const [rosterExpanded, setRosterExpanded] = useState(false);

  useEffect(() => {
    setActiveId(null);
    setSelectedSetIdx(null);
    setRosterExpanded(false);
  }, [sessionKey]);

  return {
    activeId,
    setActiveId,
    selectedSetIdx,
    setSelectedSetIdx,
    rosterExpanded,
    setRosterExpanded,
  };
}
